<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import CurrencyInput from '@/components/Globals/Inputs/CurrencyInput.vue'

const { t } = useI18n()
const store = useFareOptimizerStore()

const { reportOpportunities, isOpportunitiesCurated, opportunitiesMonthlyTotal } = storeToRefs(store)

const onText = (index: number, field: 'categoria' | 'titulo' | 'descricao' | 'acao', event: Event) => {
  const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value
  store.updateOpportunity(index, { [field]: value })
}

const onMonthly = (index: number, value: number | null) => {
  store.updateOpportunity(index, { economia_mensal: value ?? 0 })
}

const onYearly = (index: number, value: number | null) => {
  store.updateOpportunity(index, { economia_anual: value ?? 0 })
}

const addOpportunity = () => {
  store.addOpportunity({ categoria: t('contract_optimizer.opportunities.new_category') })
}
</script>

<template>
  <div class="mt-8 bg-white shadow-4">
    <div class="flex flex-wrap items-center justify-between gap-3 border-b-2 border-stroke px-6 py-4">
      <div>
        <h1 class="font-satoshi text-base font-bold text-black">
          {{ t('contract_optimizer.opportunities.title') }}
        </h1>
        <p class="mt-1 text-sm text-slate-500">{{ t('contract_optimizer.opportunities.subtitle') }}</p>
      </div>
      <div class="flex items-center gap-2">
        <span
          class="rounded-full px-3 py-1 text-xs font-medium"
          :class="isOpportunitiesCurated ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'">
          {{ isOpportunitiesCurated ? t('contract_optimizer.opportunities.curated_badge') : t('contract_optimizer.opportunities.auto_badge') }}
        </span>
        <button
          v-if="isOpportunitiesCurated"
          class="rounded border border-stroke px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
          @click="store.resetOpportunities()">
          {{ t('contract_optimizer.opportunities.reset') }}
        </button>
        <button
          class="rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-opacity-90"
          @click="addOpportunity">
          + {{ t('contract_optimizer.opportunities.add') }}
        </button>
      </div>
    </div>

    <div class="p-4 sm:p-6">
      <p v-if="reportOpportunities.length === 0" class="py-6 text-center text-sm text-body">
        {{ t('contract_optimizer.opportunities.empty') }}
      </p>

      <div v-else class="flex flex-col gap-4">
        <div
          v-for="(opp, index) in reportOpportunities"
          :key="index"
          class="border border-stroke p-4 shadow-9">
          <div class="flex items-start justify-between gap-3">
            <span class="font-satoshi text-lg font-bold text-primary">{{ String(index + 1).padStart(2, '0') }}</span>
            <button
              class="text-sm font-medium text-red-500 transition hover:text-red-600"
              @click="store.removeOpportunity(index)">
              {{ t('contract_optimizer.opportunities.remove') }}
            </button>
          </div>

          <div class="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-slate-600">{{ t('contract_optimizer.opportunities.category') }}</span>
              <input
                class="border-2 border-stroke px-2 py-1 font-satoshi text-sm text-body outline-none"
                type="text"
                :value="opp.categoria"
                :placeholder="t('contract_optimizer.opportunities.category_placeholder')"
                @change="onText(index, 'categoria', $event)" />
            </label>
            <label class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-slate-600">{{ t('contract_optimizer.opportunities.report_title') }}</span>
              <input
                class="border-2 border-stroke px-2 py-1 font-satoshi text-sm text-body outline-none"
                type="text"
                :value="opp.titulo"
                :placeholder="t('contract_optimizer.opportunities.report_title_placeholder')"
                @change="onText(index, 'titulo', $event)" />
            </label>
          </div>

          <label class="mt-3 flex flex-col gap-1 text-sm">
            <span class="font-medium text-slate-600">{{ t('contract_optimizer.opportunities.description') }}</span>
            <textarea
              class="border-2 border-stroke px-2 py-1 font-satoshi text-sm text-body outline-none"
              rows="2"
              :value="opp.descricao"
              :placeholder="t('contract_optimizer.opportunities.description_placeholder')"
              @change="onText(index, 'descricao', $event)"></textarea>
          </label>

          <label class="mt-3 flex flex-col gap-1 text-sm">
            <span class="font-medium text-slate-600">{{ t('contract_optimizer.opportunities.action') }}</span>
            <textarea
              class="border-2 border-stroke px-2 py-1 font-satoshi text-sm text-body outline-none"
              rows="2"
              :value="opp.acao"
              :placeholder="t('contract_optimizer.opportunities.action_placeholder')"
              @change="onText(index, 'acao', $event)"></textarea>
          </label>

          <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-slate-600">{{ t('contract_optimizer.opportunities.monthly') }}</span>
              <CurrencyInput
                class="border-2 border-stroke px-2 py-1 font-satoshi text-sm text-body outline-none"
                :model-value="opp.economia_mensal"
                @update:model-value="(v) => onMonthly(index, v)" />
            </label>
            <label class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-slate-600">{{ t('contract_optimizer.opportunities.yearly') }}</span>
              <CurrencyInput
                class="border-2 border-stroke px-2 py-1 font-satoshi text-sm text-body outline-none"
                :model-value="opp.economia_anual"
                @update:model-value="(v) => onYearly(index, v)" />
              <span class="text-xs text-slate-400">{{ t('contract_optimizer.opportunities.yearly_hint') }}</span>
            </label>
          </div>
        </div>
      </div>

      <div
        v-if="reportOpportunities.length > 0"
        class="mt-4 flex items-center justify-between border-t-2 border-stroke pt-4">
        <span class="font-satoshi text-base font-bold text-black">{{ t('contract_optimizer.opportunities.total') }}</span>
        <span class="font-satoshi text-base font-bold text-primary">{{ useCurrencyFormaterFromCents(opportunitiesMonthlyTotal) }}</span>
      </div>
    </div>
  </div>
</template>
